import {
  ITimeOffAttachmentParams,
  ITimeOffRequestActions,
  TimeOffContext,
  ITimeOffRequestCommentFetchActionPayload,
  ITimeOffRequestCommentUpdateActionPayload,
  ITimeOffRequestCommentAttachmentAddActionPayload,
  ITimeOffRequestCommentAttachmentRemoveActionPayload
} from './actions.types';
import {
  IMentionAttachmentCommentData,
  ITimeOffRequestData,
  IUserData
} from '@/models';
import {
  IFetchTimeOffRequestPayload,
  ITimeOffRequestPaginatedListData,
  IUpdateTimeOffStatePayload
} from '@/services/timeoff-request';
import { ITransition } from '@/models/transition';

export const actions: ITimeOffRequestActions = {
  fetchTimeOffRequests(
    context: TimeOffContext,
    payload: IFetchTimeOffRequestPayload
  ): Promise<void> {
    const promise = this.$services.timeOffRequest.fetchTimeOffRequests(payload);
    context.commit('SET_LAST_PROMISE', promise);
    return promise.then(
      (
        timeOffRequests:
          | ITimeOffRequestData[]
          | ITimeOffRequestPaginatedListData
      ) => {
        // BUG - last promise occassionally prevents unapprove time off request to be fetched
        // SOLUTION - move unapprove time off request api call outside of the last promise check
        if (payload.approvers) {
          context.commit('SET_UNAPPROVED_TIME_OFF_REQUESTS', timeOffRequests);
        } else if (promise == context.state.lastPromise) {
          if (payload.page) {
            context.commit(
              'SET_COUNT',
              (timeOffRequests as ITimeOffRequestPaginatedListData).count
            );

            context.commit(
              'SET_TIME_OFF_REQUESTS',
              (timeOffRequests as ITimeOffRequestPaginatedListData).results
            );
          } else {
            context.commit('SET_TIME_OFF_REQUESTS', timeOffRequests);
          }
          context.commit('SET_LAST_PROMISE', null);
        }
      }
    );
  },

  fetchTimeOffRequest(context: TimeOffContext, payload: number): Promise<void> {
    return this.$services.timeOffRequest
      .fetchTimeOffRequest(payload)
      .then((timeOffRequest: ITimeOffRequestData) => {
        const index = context.state.timeOffRequests.findIndex(
          (v: ITimeOffRequestData) => v.id === timeOffRequest.id
        );
        if (index === -1) {
          context.commit('ADD_TIME_OFF_REQUEST', timeOffRequest);
        } else {
          context.commit('UPDATE_TIME_OFF_REQUEST', timeOffRequest);
        }
      });
  },

  createTimeOffRequest(
    context: TimeOffContext,
    payload: ITimeOffRequestData
  ): Promise<void> {
    return this.$services.timeOffRequest
      .createTimeOffRequest(payload)
      .then((timeOffRequest: ITimeOffRequestData) =>
        context.commit('ADD_TIME_OFF_REQUEST', timeOffRequest)
      );
  },

  updateTimeOffRequest(
    context: TimeOffContext,
    payload: ITimeOffRequestData
  ): Promise<void> {
    return this.$services.timeOffRequest
      .updateTimeOffRequest(payload)
      .then((timeOffRequest: ITimeOffRequestData) => {
        const index = context.state.timeOffRequests.findIndex(
          (v: ITimeOffRequestData) => v.id === timeOffRequest.id
        );
        if (index === -1) {
          context.commit('ADD_TIME_OFF_REQUEST', timeOffRequest);
        } else {
          context.commit('UPDATE_TIME_OFF_REQUEST', timeOffRequest);
        }
      });
  },

  deleteTimeOffRequest(
    context: TimeOffContext,
    payload: number
  ): Promise<void> {
    return this.$services.timeOffRequest
      .deleteTimeOffRequest(payload)
      .then(() => {
        context.commit('REMOVE_TIME_OFF_REQUEST', payload);
      });
  },

  exportTimeOffRequestsXLSX(
    _context: TimeOffContext,
    payload: IFetchTimeOffRequestPayload
  ): Promise<Blob> {
    return this.$services.timeOffRequest.exportTimeOffRequestsXLSX(payload);
  },

  updateTimeOffRequestState: function(
    context: TimeOffContext,
    payload: IUpdateTimeOffStatePayload
  ): Promise<void> {
    return this.$services.timeOffRequest
      .updateTimeOffRequestState(payload)
      .then((timeOffRequest: ITimeOffRequestData) => {
        const index = context.state.timeOffRequests.findIndex(
          (v: ITimeOffRequestData) => v.id === timeOffRequest.id
        );
        if (index === -1) {
          context.commit('ADD_TIME_OFF_REQUEST', timeOffRequest);
        } else {
          context.commit('UPDATE_TIME_OFF_REQUEST', timeOffRequest);
        }
      });
  },

  downloadTimeOffAttachment(
    _context: TimeOffContext,
    payload: ITimeOffAttachmentParams
  ): Promise<Blob> {
    return this.$services.timeOffRequest
      .downloadTimeOffAttachment(payload.id, payload.attachmentId)
      .then(data => data);
  },

  deleteTimeOffAttachment(
    _context: TimeOffContext,
    payload: ITimeOffAttachmentParams
  ): Promise<Blob> {
    return this.$services.timeOffRequest
      .deleteTimeOffAttachment(payload.id, payload.attachmentId)
      .then(data => data);
  },

  fetchTimeOffRequestActivities(
    context: TimeOffContext,
    payload: number
  ): Promise<void> {
    return this.$services.timeOffRequest
      .fetchTimeOffRequestActivities(payload)
      .then((activities: ITransition[]) => {
        context.commit('SET_TIME_OFF_REQUEST_ACTIVITIES', activities);
      });
  },

  // TIME-OFF REQUESTS
  fetchTimeOffRequestComments(
    context: TimeOffContext,
    payload: number
  ): Promise<void> {
    return this.$services.timeOffRequest
      .fetchTimeOffRequestComments(payload)
      .then((comments: IMentionAttachmentCommentData[]) => {
        context.commit('SET_TIME_OFF_REQUEST_COMMENTS', comments);
      });
  },

  fetchTimeOffRequestComment(
    context: TimeOffContext,
    payload: ITimeOffRequestCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.timeOffRequest
      .fetchTimeOffRequestComment(payload.timeOffId, payload.commentId)
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_TIME_OFF_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_TIME_OFF_REQUEST_COMMENT', comment);
        }
      });
  },

  createTimeOffRequestComment(
    context: TimeOffContext,
    payload: ITimeOffRequestCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.timeOffRequest
      .createTimeOffRequestComment(payload.timeOffId, payload.comment)
      .then((comment: IMentionAttachmentCommentData) => {
        context.commit('SET_LAST_CREATED_REQUEST_COMMENT_ID', comment.id);
        context.commit('ADD_TIME_OFF_REQUEST_COMMENT', comment);
      });
  },

  updateTimeOffRequestComment(
    context: TimeOffContext,
    payload: ITimeOffRequestCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.timeOffRequest
      .updateTimeOffRequestComment(payload.timeOffId, payload.comment)
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_TIME_OFF_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_TIME_OFF_REQUEST_COMMENT', comment);
        }
      });
  },

  deleteTimeOffRequestComment(
    context: TimeOffContext,
    payload: ITimeOffRequestCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.timeOffRequest
      .deleteTimeOffRequestComment(payload.timeOffId, payload.commentId)
      .then(() => {
        context.commit('REMOVE_TIME_OFF_REQUEST_COMMENT', payload.commentId);
      });
  },

  addTimeOffRequestCommentAttachments(
    context: TimeOffContext,
    payload: ITimeOffRequestCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.timeOffRequest
      .addTimeOffRequestCommentAttachments(
        payload.timeOffId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_TIME_OFF_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_TIME_OFF_REQUEST_COMMENT', comment);
        }
      });
  },

  removeTimeOffRequestCommentAttachment(
    context: TimeOffContext,
    payload: ITimeOffRequestCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.timeOffRequest
      .removeTimeOffRequestCommentAttachment(
        payload.timeOffId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_TIME_OFF_REQUEST_COMMENT', comment);
        }
      });
  },

  // Mentions
  fetchTimeOffRequestCommentsMentionableUsers(
    context: TimeOffContext,
    payload: number
  ): Promise<void> {
    return this.$services.timeOffRequest
      .fetchTimeOffRequestCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_MENTIONABLE_USERS', mentionableUsers);
      });
  }
};
